// frontend/src/pages/ServiceDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiService, getFullImageUrl } from '../services/api';
import SEOHead from '../components/SEOHead';
import { 
  ArrowRight, 
  Phone, 
  Mail, 
  Clock, 
  DollarSign, 
  Users,
  Settings,
  Wrench,
  BookOpen,
  PhoneCall,
  ChevronLeft,
  ChevronRight,
  Star,
  Check,
  Plus,
  Minus,
  Calendar,
  FileText,
  Download,
  Eye
} from 'lucide-react';

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [relatedServices, setRelatedServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedFaqs, setExpandedFaqs] = useState(new Set());
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    company: '',
    email: '',
    phone: '',
    project_description: '',
    budget: '',
    deadline: '',
  });

  useEffect(() => {
    if (slug){
      fetchServiceData();
    }
  }, [slug]);

  const fetchServiceData = async () => {
    try {
      setLoading(true);
      
      // دریافت اطلاعات خدمت
      const serviceResponse = await apiService.getService(slug);
      const services = serviceResponse.data.results || serviceResponse.data;
      
      if (services.length === 0) {
        throw new Error('Service not found');
      }

      const serviceData = services[0];
      setService(serviceData);

      // افزایش تعداد بازدید
      //await apiService.incrementServiceView(serviceData.id);

      // خدمات مرتبط از طریق API مربوطه
      if (serviceData.related_services) {
        setRelatedServices(serviceData.related_services);
      }

    } catch (error) {
      console.error('Error fetching service data:', error);
      navigate('/services');
    } finally {
      setLoading(false);
    }
  };

  const toggleFaq = (faqId) => {
    const newExpandedFaqs = new Set(expandedFaqs);
    if (newExpandedFaqs.has(faqId)) {
      newExpandedFaqs.delete(faqId);
    } else {
      newExpandedFaqs.add(faqId);
    }
    setExpandedFaqs(newExpandedFaqs);
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    try {
      await apiService.submitServiceRequest({
        ...formData,
        service: service.id
      });
      
      alert('درخواست شما با موفقیت ثبت شد. به زودی با شما تماس خواهیم گرفت.');
      setShowRequestForm(false);
      setFormData({
        full_name: '',
        company: '',
        email: '',
        phone: '',
        project_description: '',
        budget: '',
        deadline: '',
      });
    } catch (error) {
      console.error('Error submitting service request:', error);
      alert('خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.');
    }
  };

  const nextImage = () => {
    if (service.images && service.images.length > 0) {
      setSelectedImage((prev) => 
        prev === service.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (service.images && service.images.length > 0) {
      setSelectedImage((prev) => 
        prev === 0 ? service.images.length - 1 : prev - 1
      );
    }
  };

  const getServiceTypeIcon = (type) => {
    const icons = {
      'consulting': Users,
      'implementation': Settings,
      'support': PhoneCall,
      'training': BookOpen,
      'maintenance': Wrench,
    };
    return icons[type] || Settings;
  };

  const getServiceTypeLabel = (type) => {
    const labels = {
      'consulting': 'مشاوره',
      'implementation': 'اجرا',
      'support': 'پشتیبانی',
      'training': 'آموزش',
      'maintenance': 'نگهداری',
    };
    return labels[type] || type;
  };

  // تابع برای تبدیل HTML به JSX (برای ویژگی‌ها و مزایا)
  const renderHTML = (html) => {
    return { __html: html };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-white mb-2">خدمت یافت نشد</h2>
          <Link to="/services" className="text-yellow-400 hover:text-yellow-300">
            بازگشت به خدمات
          </Link>
        </div>
      </div>
    );
  }

  const TypeIcon = getServiceTypeIcon(service.service_type);
  const mainImage = service.images && service.images.length > 0 
    ? getFullImageUrl(service.images[selectedImage]?.image)
    : getFullImageUrl(service.featured_image);

  // داده‌های ساختاریافته برای خدمت
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.short_description,
    "provider": {
      "@type": "Organization",
      "name": "BusinessPro"
    },
    "areaServed": "IR",
    "serviceType": getServiceTypeLabel(service.service_type)
  };

  return (
    <>
      <SEOHead 
        title={`${service.name} - خدمات Spadana Techno Poltmer`}
        description={service.short_description}
        keywords={`${service.name}, ${service.category?.name}قالب سازی، لاستیک سازی، طراحی و ساخت انواع قالب، تولید قطعات پلاستیکی، `}
        image={getFullImageUrl(service.featured_image)}
        type="service"
      >
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </SEOHead>
      
      <div className="min-h-screen bg-black py-8">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 space-x-reverse text-sm text-gray-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors">خانه</Link>
            <ArrowRight size={16} />
            <Link to="/services" className="hover:text-white transition-colors">خدمات</Link>
            <ArrowRight size={16} />
            {service.category && (
              <>
                <Link 
                  to={`/services?category=${service.category.slug}`} 
                  className="hover:text-white transition-colors"
                >
                  {service.category.name}
                </Link>
                <ArrowRight size={16} />
              </>
            )}
            <span className="text-yellow-400">{service.name}</span>
          </nav>

          {/* Service Main Section */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 md:p-8">
              {/* Service Images */}
              <div className="space-y-4">
                {/* Main Image */}
                <div className="relative bg-gray-800 rounded-2xl overflow-hidden">
                  <img
                    src={mainImage}
                    alt={service.name}
                    className="w-full h-80 md:h-96 object-cover"
                  />
                  
                  {/* Navigation Arrows */}
                  {service.images && service.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                      >
                        <ChevronRight size={24} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                      >
                        <ChevronLeft size={24} />
                      </button>
                    </>
                  )}

                  {/* Featured Badge */}
                  {service.is_featured && (
                    <div className="absolute top-4 right-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-bold">
                      خدمت ویژه
                    </div>
                  )}
                </div>

                {/* Thumbnail Images */}
                {service.images && service.images.length > 1 && (
                  <div className="flex space-x-3 space-x-reverse overflow-x-auto pb-2">
                    {service.images.map((image, index) => (
                      <button
                        key={image.id}
                        onClick={() => setSelectedImage(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          selectedImage === index 
                            ? 'border-yellow-400' 
                            : 'border-gray-700 hover:border-gray-500'
                        }`}
                      >
                        <img
                          src={getFullImageUrl(image.image)}
                          alt={image.title || `${service.name} - تصویر ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Service Info */}
              <div className="space-y-6">
                <div>
                  <div className="flex items-center space-x-3 space-x-reverse mb-4">
                    <TypeIcon className="text-yellow-400" size={24} />
                    <span className="text-yellow-400 font-medium capitalize">
                      {getServiceTypeLabel(service.service_type)}
                    </span>
                  </div>

                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    {service.name}
                  </h1>
                  
                  <div className="flex items-center space-x-4 space-x-reverse text-gray-400 text-sm mb-4">
                    <div className="flex items-center space-x-1 space-x-reverse">
                      <Eye size={16} />
                      <span>{service.view_count} بازدید</span>
                    </div>
                    {service.category && (
                      <Link 
                        to={`/services?category=${service.category.slug}`}
                        className="text-yellow-400 hover:text-yellow-300"
                      >
                        {service.category.name}
                      </Link>
                    )}
                  </div>
                </div>

                {/* Service Meta */}
                <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                  <h3 className="font-bold text-white mb-4">اطلاعات خدمت</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {service.duration && (
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <Clock className="text-yellow-400" size={20} />
                        <div>
                          <div className="text-gray-400 text-sm">مدت زمان</div>
                          <div className="text-white font-medium">{service.duration}</div>
                        </div>
                      </div>
                    )}
                    {service.price_range && (
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <DollarSign className="text-yellow-400" size={20} />
                        <div>
                          <div className="text-gray-400 text-sm">بازه قیمتی</div>
                          <div className="text-white font-medium">{service.price_range}</div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <Calendar className="text-yellow-400" size={20} />
                      <div>
                        <div className="text-gray-400 text-sm">پشتیبانی</div>
                        <div className="text-white font-medium">۲۴/۷</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <Check className="text-yellow-400" size={20} />
                      <div>
                        <div className="text-gray-400 text-sm">گارانتی</div>
                        <div className="text-white font-medium">دارد</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Short Description */}
                <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                  <h3 className="font-bold text-white mb-3">توضیحات کوتاه</h3>
                  <p className="text-gray-300 leading-relaxed">
                    {service.short_description}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowRequestForm(true)}
                    className="flex-1 bg-yellow-400 text-gray-900 py-4 px-6 rounded-xl font-bold hover:bg-yellow-300 transition-all flex items-center justify-center space-x-2 space-x-reverse"
                  >
                    <FileText size={20} />
                    <span>درخواست خدمت</span>
                  </button>
                  
                  <button className="flex-1 border-2 border-yellow-400 text-yellow-400 py-4 px-6 rounded-xl font-bold hover:bg-yellow-400 hover:text-gray-900 transition-all flex items-center justify-center space-x-2 space-x-reverse">
                    <Phone size={20} />
                    <span>مشاوره رایگان</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Service Details Tabs */}
          <div className="bg-gray-900 rounded-2xl border border-gray-800 mb-8">
            {/* Tab Navigation */}
            <div className="border-b border-gray-800">
              <nav className="flex space-x-8 space-x-reverse px-6 overflow-x-auto">
                {[
                  { id: 'overview', label: 'نمای کلی' },
                  { id: 'features', label: 'ویژگی‌ها' },
                  { id: 'benefits', label: 'مزایا' },
                  { id: 'faq', label: 'سوالات متداول' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 py-4 border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-yellow-500 text-yellow-400'
                        : 'border-transparent text-gray-400 hover:text-white'
                    }`}
                  >
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="prose prose-invert max-w-none">
                  <div 
                    className="text-gray-300 leading-8"
                    dangerouslySetInnerHTML={renderHTML(service.full_description)}
                  />
                </div>
              )}

              {activeTab === 'features' && (
                <div className="space-y-6">
                  {service.features ? (
                    <div 
                      className="text-gray-300 leading-8 prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={renderHTML(service.features)}

                    />
                  ) : (
                    <div className="text-center py-8">
                      <Settings className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">ویژگی‌های این خدمت به زودی اضافه خواهد شد.</p>
                    </div>
                  )}
                  
                  {/* ویژگی‌های کلی */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <Check className="text-green-400 mb-2" size={24} />
                      <h4 className="font-bold text-white mb-2">کیفیت تضمینی</h4>
                      <p className="text-gray-300 text-sm">تضمین کیفیت خدمات با استانداردهای بین‌المللی</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <Users className="text-blue-400 mb-2" size={24} />
                      <h4 className="font-bold text-white mb-2">تیم متخصص</h4>
                      <p className="text-gray-300 text-sm">تیم متشکل از کارشناسان با تجربه و متخصص</p>
                    </div>
                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <PhoneCall className="text-purple-400 mb-2" size={24} />
                      <h4 className="font-bold text-white mb-2">پشتیبانی ۲۴/۷</h4>
                      <p className="text-gray-300 text-sm">پشتیبانی دائمی حتی پس از اتمام پروژه</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'benefits' && (
                <div className="space-y-6">
                  {service.benefits ? (
                    <div 
                      className="text-gray-300 leading-8 prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={renderHTML(service.benefits)}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <Star className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">مزایای این خدمت به زودی اضافه خواهد شد.</p>
                    </div>
                  )}
                  
                  {/* مزایای کلی */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                    <div className="bg-gray-800 p-6 rounded-lg border border-yellow-400">
                      <h4 className="font-bold text-yellow-400 mb-3">مزایای کسب‌وکار</h4>
                      <ul className="space-y-2 text-gray-300">
                        <li className="flex items-center space-x-2 space-x-reverse">
                          <Check className="text-green-400" size={16} />
                          <span>افزایش بهره‌وری و کارایی</span>
                        </li>
                        <li className="flex items-center space-x-2 space-x-reverse">
                          <Check className="text-green-400" size={16} />
                          <span>کاهش هزینه‌های عملیاتی</span>
                        </li>
                        <li className="flex items-center space-x-2 space-x-reverse">
                          <Check className="text-green-400" size={16} />
                          <span>بهبود کیفیت محصولات</span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-gray-800 p-6 rounded-lg border border-yellow-400">
                      <h4 className="font-bold text-yellow-400 mb-3">مزایای فنی</h4>
                      <ul className="space-y-2 text-gray-300">
                        <li className="flex items-center space-x-2 space-x-reverse">
                          <Check className="text-green-400" size={16} />
                          <span>استفاده از تکنولوژی‌های روز</span>
                        </li>
                        <li className="flex items-center space-x-2 space-x-reverse">
                          <Check className="text-green-400" size={16} />
                          <span>راه‌حل‌های مقیاس‌پذیر</span>
                        </li>
                        <li className="flex items-center space-x-2 space-x-reverse">
                          <Check className="text-green-400" size={16} />
                          <span>امنیت و قابلیت اطمینان بالا</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'faq' && (
                <div className="space-y-4">
                  {service.faqs && service.faqs.length > 0 ? (
                    service.faqs.map((faq) => (
                      <div key={faq.id} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                        <button
                          onClick={() => toggleFaq(faq.id)}
                          className="w-full p-4 text-right flex items-center justify-between hover:bg-gray-700 transition-colors"
                        >
                          <span className="font-medium text-white text-lg">{faq.question}</span>
                          {expandedFaqs.has(faq.id) ? (
                            <Minus className="text-yellow-400 flex-shrink-0" size={20} />
                          ) : (
                            <Plus className="text-yellow-400 flex-shrink-0" size={20} />
                          )}
                        </button>
                        {expandedFaqs.has(faq.id) && (
                          <div className="p-4 border-t border-gray-700">
                            <div 
                              className="text-gray-300 leading-7 prose prose-invert max-w-none"
                              dangerouslySetInnerHTML={renderHTML(faq.answer)}
                            />
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <BookOpen className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">سوالات متداول این خدمت به زودی اضافه خواهد شد.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Request Form Modal */}
          {showRequestForm && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
              <div className="bg-gray-900 rounded-2xl border border-gray-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-800">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">درخواست خدمت: {service.name}</h3>
                    <button
                      onClick={() => setShowRequestForm(false)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <span className="text-2xl">×</span>
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmitRequest} className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-300 mb-2">نام کامل *</label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        value={formData.full_name}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                        placeholder="نام و نام خانوادگی"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">شرکت</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                        placeholder="نام شرکت (اختیاری)"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-300 mb-2">ایمیل *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">تلفن *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                        placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">توضیحات پروژه *</label>
                    <textarea
                      name="project_description"
                      required
                      rows="4"
                      value={formData.project_description}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      placeholder="لطفاً پروژه خود را به طور کامل شرح دهید..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-300 mb-2">بودجه پیش‌بینی شده</label>
                      <input
                        type="text"
                        name="budget"
                        value={formData.budget}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                        placeholder="مثال: ۱۰-۵۰ میلیون تومان"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">ددلاین مورد نظر</label>
                      <input
                        type="text"
                        name="deadline"
                        value={formData.deadline}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                        placeholder="مثال: ۲ ماه"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 space-x-reverse pt-6 border-t border-gray-800">
                    <button
                      type="submit"
                      className="bg-yellow-400 text-gray-900 px-6 py-3 rounded-lg font-bold hover:bg-yellow-300 transition-colors"
                    >
                      ارسال درخواست
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRequestForm(false)}
                      className="bg-gray-700 text-gray-300 px-6 py-3 rounded-lg font-bold hover:bg-gray-600 transition-colors"
                    >
                      انصراف
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Related Services */}
          {relatedServices.length > 0 && (
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6">
              <h2 className="text-2xl font-bold text-white mb-6">خدمات مرتبط</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedServices.map((relatedService) => (
                  <Link
                    key={relatedService.id}
                    to={`/services/${relatedService.slug}`}
                    className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-yellow-400 transition-all group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={getFullImageUrl(relatedService.featured_image)}
                        alt={relatedService.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      {relatedService.is_featured && (
                        <div className="absolute top-2 left-2 bg-yellow-400 text-gray-900 px-2 py-1 rounded text-xs font-bold">
                          ویژه
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-white text-sm mb-2 line-clamp-2 group-hover:text-yellow-400 transition-colors">
                        {relatedService.name}
                      </h3>
                      <p className="text-gray-400 text-xs line-clamp-2 mb-3">
                        {relatedService.short_description}
                      </p>
                      <div className="flex items-center justify-between text-gray-400 text-xs">
                        <span>{getServiceTypeLabel(relatedService.service_type)}</span>
                        <span>{relatedService.view_count} بازدید</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ServiceDetail;